/**
 * Progression du Guide : les sujets cochés « fait », par navigateur
 * (localStorage, pas de table : c'est un référentiel global, la coche est
 * un confort de lecture, pas une donnée du client).
 *
 * Petit magasin externe (useSyncExternalStore) : le volet, les repères et
 * n'importe quel écran lisent la même progression sans se la passer.
 */
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'palestre_onboarding_done_v1';
/* Le dernier guide consulté, pour que l'Accueil propose de le reprendre là où
   on l'a laissé : { topicId, step }. Même magasin, même navigateur. */
const LAST_KEY = 'palestre_guide_last_v1';

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function readLast() {
  try {
    const raw = window.localStorage.getItem(LAST_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && parsed.topicId ? parsed : null;
  } catch {
    return null;
  }
}

let done = read();
let last = readLast();
const subs = new Set();
function emit() { subs.forEach((fn) => fn()); }
function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
/* Un seul instantané pour les deux (sujets cochés, dernier consulté) : il ne
   change d'identité que lorsque l'un des deux bouge. */
let snap = { done, last };
function bump() { snap = { done, last }; emit(); }
function snapshot() { return snap; }

export function isDone(topicId) { return !!done[topicId]; }

export function setDone(topicId, value) {
  const next = { ...done };
  if (value) next[topicId] = true; else delete next[topicId];
  done = next;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(done)); } catch { /* stockage indisponible */ }
  if (value && last && last.topicId === topicId) last = null;   // terminé : plus rien à reprendre
  bump();
}

/** Mémorise où l'on en est d'un guide (appelé par le mode Guide). */
export function setLastGuide(topicId, step) {
  if (!topicId) return;
  if (last && last.topicId === topicId && last.step === step) return;
  last = { topicId, step };
  try { window.localStorage.setItem(LAST_KEY, JSON.stringify(last)); } catch { /* quota */ }
  bump();
}

/** Hook : { done, last } — les sujets cochés et le dernier guide consulté. */
export function useProgressStore() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Hook : la table des sujets cochés (objet { [topicId]: true }). */
export function useProgress() {
  return useProgressStore().done;
}
